import { AfterViewInit, Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Store } from '@ngxs/store';
import { environment } from 'environments/environment';
import { forkJoin, Observable, combineLatest, map, distinctUntilChanged, filter, pairwise } from 'rxjs';
import { LenderUser } from './models/dto/lender-user.model';
import { PusherService } from './modules/shared/services/pusher.service';
import { RollbarService } from './rollbar';
import {
  GetLenderUser,
  TogglePermission,
} from './store/lender-user/lender-user/lender-user.actions';
import { LenderUserState } from './store/lender-user/lender-user/lender-user.state';
import { Router, NavigationStart } from '@angular/router';
import { AuthenticationService } from './modules/session/services/authentication.service';
import { isMobileDevice } from 'app/util/mobile/isMobile';
import { isChromeBrowser } from 'app/util/browser/chrome';

import Rollbar from 'rollbar';
import { GetDealStatuses } from './store/deal/statuses/deal-statuses.actions';
import { GetActiveFeatureFlags } from 'ng2-app/store/feature-flags/feature-flags.actions';
import { LoadNeedsDisposition } from './store/disposition/disposition.actions';
import { DispositionState } from './store/disposition/disposition.state';
import { CallState } from './store/call/call.state';
import { OPEN_BORROWER_MESSAGE } from 'ng2-app/sales-inventory/shared/borrower-tab';

declare global {
  interface Window {
    togglePermission?: (permission: string) => void;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {

  @ViewChild('globalCxSideNav') cxDrawer: MatDrawer;

  lenderUser$: Observable<LenderUser | null> = this.store.select(LenderUserState.lenderUser);

  needsDisposition$ = this.store.select(DispositionState.needsDisposition);
  private activeCall$ = this.store.select(CallState.call);
  private dispositionCall$ = this.store.select(CallState.dispositionCall);
  private onCall$ = this.activeCall$.pipe(
    map(call => !!call && call.callState !== 'disconnected' && call.callState !== 'canceled'),
    distinctUntilChanged(),
  );

  interactionCallId$ = combineLatest([
    this.activeCall$,
    this.needsDisposition$,
    this.dispositionCall$,
  ]).pipe(
    map(([call, needsDisposition, dispositionCall]) =>
      call?.callId ?? call?.id ?? dispositionCall?.callId ?? needsDisposition?.callId ?? null),
  );

  dispositionLocked$: Observable<boolean> = combineLatest([
    this.store.select(LenderUserState.lenderUser),
    this.needsDisposition$,
    this.onCall$,
  ]).pipe(
    map(([lenderUser, needsDisposition, onCall]) =>
      !!lenderUser?.team?.type?.includes('customer_service') &&
      !!needsDisposition?.hasOpen &&
      !onCall
    ),
  );

  // Non-CS parity to dispositionLocked$: non-CS reps get the same nav/unload
  // lock while a disposition is open, but their modal is the WrapUpDialog
  // opened by call-display-smart (not the CS forced drawer).
  nonCsDispositionPending$: Observable<boolean> = combineLatest([
    this.store.select(LenderUserState.lenderUser),
    this.needsDisposition$,
    this.onCall$,
  ]).pipe(
    map(([lenderUser, needsDisposition, onCall]) =>
      !lenderUser?.team?.type?.includes('customer_service') &&
      !!needsDisposition?.hasOpen &&
      !onCall
    ),
  );

  forcedPrefill$ = this.dispositionLocked$.pipe(
    distinctUntilChanged(),
    filter(locked => locked),
    map(() => {
      const activeCall = this.store.selectSnapshot(CallState.call);
      const dispositionCall = this.store.selectSnapshot(CallState.dispositionCall);
      const needsDisposition = this.store.selectSnapshot(DispositionState.needsDisposition);
      const call = activeCall ?? dispositionCall;

      const liveBorrowerId = call?.borrowerId && call.borrowerId !== -1 ? call.borrowerId : null;
      const restoredBorrowerId = Number(needsDisposition?.borrowerId);
      const borrowerId = liveBorrowerId
        ?? (Number.isFinite(restoredBorrowerId) && restoredBorrowerId > 0 ? restoredBorrowerId : null);

      const direction = call?.type ?? needsDisposition?.callDirection ?? null;
      const interactionType = direction
        ? (direction === 'incoming' ? 'CX Inbound Call' : 'CX Outbound Call')
        : null;

      return { borrowerId, interactionType };
    }),
  );

  livePrefill$ = this.onCall$.pipe(
    pairwise(),
    filter(([wasOnCall, isOnCall]) => !wasOnCall && isOnCall),
    map(() => {
      const call = this.store.selectSnapshot(CallState.call);
      const borrowerId = call?.borrowerId && call.borrowerId !== -1 ? call.borrowerId : null;
      const direction = call?.type ?? null;
      const interactionType = direction
        ? (direction === 'incoming' ? 'CX Inbound Call' : 'CX Outbound Call')
        : null;
      return { borrowerId, interactionType };
    }),
    filter(prefill => prefill.borrowerId != null || prefill.interactionType != null),
  );

  private dispositionLocked = false;
  private nonCsDispositionPending = false;
  private pusherHasConnected = false;
  private wrapUpChannel = new BroadcastChannel('wrapup_channel');

  canSubmitCxInteractions$: Observable<boolean>;
  suppressNotifications: boolean = false;
  title = 'pipeline';
  siteIsUpdated = false;
  globalHeaderLoaded = false;
  loginPage = false;
  isSupported: boolean;
  canViewCxInteractions: boolean = false;
  showGlobalBorrowersTabs: boolean = true;

  constructor(
    @Inject(RollbarService) private rollbar: Rollbar,
    private store: Store,
    private pusherService: PusherService,
    private auth: AuthenticationService,
    private router: Router
  ) {
    router.events.forEach((event) => {
      this.globalBorrowersTabs(location.pathname);

      if (event instanceof NavigationStart && (this.dispositionLocked || this.nonCsDispositionPending) && event.url !== this.router.url) {
        this.router.navigateByUrl(this.router.url, { skipLocationChange: true });
        return;
      }

      if (event instanceof NavigationStart && this.siteIsUpdated && !this.callInProgress()) {
        this.siteIsUpdated = false;
        location.reload();
      }
    });
  }

  ngAfterViewInit(): void {
    this.dispositionLocked$.subscribe(locked => {
      this.dispositionLocked = locked;
      if (locked) {
        this.cxDrawer?.open();
      }
    });
    this.nonCsDispositionPending$.subscribe(pending => {
      this.nonCsDispositionPending = pending;
    });
  }

  onCxDrawerCloseRequested(drawer: MatDrawer): void {
    if (!this.dispositionLocked) {
      drawer.close();
    }
  }

  onForcedDispositionComplete(): void {
    this.store.dispatch(new LoadNeedsDisposition(true));
    this.wrapUpChannel.postMessage({ action: 'forcedDispositionComplete' });
  }

  /** Sales Inventory reuses this tab for "open borrower" by messaging it instead of reloading it. */
  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    const { type, borrowerId } = event.data ?? {};
    if (event.origin === window.location.origin && type === OPEN_BORROWER_MESSAGE && Number.isInteger(borrowerId)) {
      this.router.navigateByUrl(`/borrower/${borrowerId}`);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dispositionLocked || this.nonCsDispositionPending) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  ngOnInit() {
    window.togglePermission = (permission) => this.togglePermission(permission);

    if (this.auth.isAuthenticated()) {
      this.store.dispatch(new GetDealStatuses());
      forkJoin([
        this.store.dispatch(new GetLenderUser()),
        this.store.dispatch(new GetActiveFeatureFlags()),
      ]).subscribe({
        next: () => this.store.dispatch(new LoadNeedsDisposition()),
        error: (err) => this.rollbar.error('lenderUser/featureFlags hydration failed', err),
      });

      this.onCall$.pipe(pairwise()).subscribe(([wasOnCall, isOnCall]) => {
        if (wasOnCall && !isOnCall) {
          this.store.dispatch(new LoadNeedsDisposition(true));
        }
      });

      this.subscribeToGlobalChannel();

      this.pusherService.connectionState
        .pipe(filter(({ current }) => current === 'connected'))
        .subscribe(() => {
          if (this.pusherHasConnected) {
            this.store.dispatch(new LoadNeedsDisposition(true));
          }
          this.pusherHasConnected = true;
        });

      this.canSubmitCxInteractions$ = this.lenderUser$.pipe(
        map(currentUser => !!currentUser ? (
            currentUser.team?.type?.includes('customer_service')
            || currentUser.team?.name?.includes('Test'))
            : false)
      );
      this.canSubmitCxInteractions$.subscribe(result => {this.canViewCxInteractions = result;});
    } else {
      this.loginPage = true;
    }

    window.addEventListener('message', (event) => {
      if (event.data?.route) {
        this.router.navigateByUrl(event.data.route); // Perform route change
      }
    });

    const isChrome = isChromeBrowser();
    const isMobile = isMobileDevice();
    this.isSupported = isChrome && !isMobile;
  }

  togglePermission(permission) {
    this.store.dispatch( new TogglePermission(permission) );
  }

  rollbarInfo() {
    // Needs the rollbar object from the constructor.
    this.rollbar.info('dat new pipeline test log');
  }

  throwError() {
    // and will still log to Rollbar.
    throw new Error('dat new pipeline test error');
  }

  subscribeToGlobalChannel() {
    this.pusherService
      .subscribeRaw(`LS-global-${environment.environment}-channel`)
      .bind('siteUpdate', () => this.siteIsUpdated = true);
  }

  callInProgress() {
    return JSON.parse(localStorage.getItem('currentCall') || '{}')?.callId ? true : false;
  }

  headerLoaded() {
    setTimeout(() => this.globalHeaderLoaded = true, 0);
  }

  globalBorrowersTabs(currentPath: string) {
    const excludedPaths = [
      '/priority',
    ];

    const excludedSections = [
      '/workflows',
    ];

    if (excludedPaths.includes(currentPath)) {
      this.showGlobalBorrowersTabs = false;
      this.suppressNotifications = true;
      return;
    }

    this.showGlobalBorrowersTabs = !excludedSections.some(section => currentPath.startsWith(section));
    this.suppressNotifications = false;
  }

  handleClickedOutsideNotifications(notificationsSidebar): void {
    if (notificationsSidebar.opened) {
      notificationsSidebar.close();
    }
  }
}
